/**
 * @fileoverview Icon component that renders icons from a sprite sheet.
 * Handles positioning within the sprite sheet based on the icon name.
 *
 * @example
 * // Basic usage
 * <Icon name="quiet-place" />
 *
 * // With additional props
 * <Icon name="on-my-own" className="additional-class" onClick={() => {}} />
 */

import config from '../config'
import css from './Icon.module.css'

const ICON_WIDTH = 32

/**
 * @function calcIconOffset
 * @description Calculates the object position for an icon based on its index in the sprite sheet.
 * @param {number} index - The index of the icon in the sprite sheet.
 * @returns {string} The calculated object position.
 */
function calcIconOffset(index: number): string {
    return `calc(-${ICON_WIDTH * index}px - ${ICON_WIDTH * index * 0.75}px) 0px`
}

const ICONS = [
    'quiet-place',
    'have-time',
    'on-my-own',
    'no-notes',
    'have-aid',
    'sound',
    'headset',
    'bluetooth',
    'file',
    'hourglass',
    'satisfied1',
    'satisfied2',
    'satisfied3',
    'satisfied4',
    'satisfied5',
    'question',
    'envelope',
    'envelope-open',
]

/**
 * @interface IconProps
 * @description Props for the Icon component.
 * @extends {React.HTMLAttributes<HTMLImageElement>}
 * @property {keyof typeof Novoic.SupportedIcons} name - The name of the icon to render.
 */
interface IconProps extends React.HTMLAttributes<HTMLImageElement> {
    name: keyof typeof Novoic.SupportedIcons
}

/**
 * @function Icon
 * @description Renders an icon from a sprite sheet based on the provided name.
 * @param {IconProps} props - The props for the Icon component.
 * @returns {JSX.Element} The rendered Icon component.
 */
export function Icon(props: IconProps) {
    return (
        <img
            alt={`icon ${props.name}`}
            className={css.icon}
            style={{ objectPosition: calcIconOffset(ICONS.indexOf(props.name)) }}
            src={config.s3.getStaticURL('icons.svg')}
        />
    )
}
